import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * AccessToken model — a PERSON-scoped `ocl_` bearer credential, hashed at rest
 * (OL-113).
 *
 * The plaintext is shown once at mint time and never persisted; only its
 * SHA-256 hex digest is stored. `tokenHash` is {@link Model.Sensitive}, which
 * drops it from the `json` / `jsonCreate` / `jsonUpdate` variants entirely.
 * That is the MECHANICAL guard behind "an owner can list their tokens, they can
 * never read one back": the rpc that lists tokens later cannot leak the digest
 * even by accident, because the key is not in the schema to serve. The digest
 * still exists on the `select` / `insert` variants, where the middleware
 * resolves a presented bearer by it.
 *
 * `customerId` is nullable on purpose and is the one nullable scope in this
 * wave: the row is a *person's* credential, not a customer's resource. `null`
 * means the token resolves to whichever customer the person's memberships
 * resolve to; a set value pins it to one customer.
 *
 * The OAuth 2.1 grant columns follow the same rule: `refreshTokenHash`,
 * `previousRefreshTokenHash` and `refreshRotationSalt` are Sensitive, so a
 * listed token never carries a refresh digest or the rotation key.
 *
 * `personId` and `customerId` are plain strings with no foreign key: `people` /
 * `customers` live in `public` while this table lives in `orcaloops`, and the
 * reference is by id only so the schema can be split out.
 *
 * @since 1.0.0
 */
export class AccessToken extends Model.Class<AccessToken>("AccessToken")({
  id: Model.Generated(Schema.String),

  /** SHA-256 hex digest of the plaintext. Absent from every JSON variant (Sensitive). */
  tokenHash: Model.Sensitive(Schema.String),

  /** The person the token acts as, and the tenancy boundary for revocation. */
  personId: Schema.String,
  /** `null` = spans the person's memberships; set = pinned to one customer. */
  customerId: Schema.NullOr(Schema.String),
  /** Human label, so the owner can recognise and revoke a token. */
  label: Schema.String,

  createdAt: Model.DateTimeInsertFromDate,

  /** Best-effort liveness marker, stamped on each successful auth. */
  lastUsedAt: Schema.NullOr(Schema.DateTimeUtc),
  /** `null` = no expiry; revocation is the control. */
  expiresAt: Schema.NullOr(Schema.DateTimeUtc),
  /** Set when the owner retires the token. The audit row is kept, never deleted. */
  revokedAt: Schema.NullOr(Schema.DateTimeUtc),

  /** The OAuth client the grant was issued to; `null` on a hand-minted token. */
  oauthClientId: Schema.NullOr(Schema.String),
  /** Digest of the CURRENT refresh token. Absent from every JSON variant (Sensitive). */
  refreshTokenHash: Model.Sensitive(Schema.NullOr(Schema.String)),
  /** Digest the latest rotation replaced, for the grace window. Sensitive. */
  previousRefreshTokenHash: Model.Sensitive(Schema.NullOr(Schema.String)),
  /** When the refresh token last rotated. */
  refreshRotatedAt: Schema.NullOr(Schema.DateTimeUtc),
  /** The latest rotation's secret HMAC key. Sensitive — never served. */
  refreshRotationSalt: Model.Sensitive(Schema.NullOr(Schema.String)),
}) {}
